<template lang="pug">
.page-header-smart-drive
  IQToolMiddlePaneHeader.header(:header="$t(`${baseTranslationPath}.header`)"
        :title="pageTitle"
        @goToListPage="navigateAway(false)"
        )

EditArea
  template(#content)
    .field-container.full-width.is-required(v-if="!isSharePoint")
      label {{ $t(`${baseTranslationPath}.title`) }}
      aiq-input(v-model="record.name"
                :placeholder="$t(`${baseTranslationPath}.title_placeholder`)"
                :max-length="250")

    .field-container.full-width.is-required.flex-display.img-gap(v-if="showFileInput")
      .file-input
        label {{ recordKind === 'file' ? $t(`${baseTranslationPath}.file`) : $t(`${baseTranslationPath}.folder`) }} {{uploadForbidden ? $t(`${baseTranslationPath}.upload_disallowed`) : ''}}
        .tiny-info {{ tinyInfo }}
        FileUploader(
          v-if="showUploader"
          :acceptedFileType="acceptedFileType"
          :multiple="recordKind === 'folder'"
          :disable="uploadForbidden || isSharePoint"
          :fileList="fileList"
          :supported_files="supported_files"
          @getRecordName="getRecordName"
          @getFileList="getFileList"
        )

    .field-container.full-width.is-required(v-if="['url', 'html', 'urllist', 'hubspot'].includes(recordKind)")
      label {{ $t(`${baseTranslationPath}.url`) }}
      aiq-input(v-model="documentUrl"
                :disabled="disableUrl"
                :max-length="250")
    .field-container.full-width.padding-top(v-if="recordKind === 'sftp'")
        label {{ $t(`${baseTranslationPath}.sftp_path`) }}
        aiq-input(v-model="sftpPath"
                  :disabled="disableUrl"
                  :max-length="250")
    .field-container.full-width.flex-display.gap-between(v-if="hideSegment")
      aiq-switch.switch(v-model="publicCheck" :disabled="!isNew")
      label {{ $t(`${baseTranslationPath}.internal_only`) }}
      aiq-tooltip.help-icon(placement="right"
                          :show-after="1000"
                          :content="$t(`${baseTranslationPath}.public_tooltip`)"
                          )
          fa-icon.question-mark(:icon="['fas', 'circle-question']")

    .field-container.full-width.flex-display.gap-between(v-if="hideSegment")
        aiq-switch.switch(v-model="upload_faq")
        label {{ $t(`${baseTranslationPath}.smart_lynq`) }}
        aiq-tooltip.help-icon(placement="right"
                            :show-after="1000"
                            :content="$t(`${baseTranslationPath}.smart_lynq_tooltip`)"
                            )
          fa-icon.question-mark(:icon="['fas', 'circle-question']")
    .smart-assist-segment(v-if="upload_faq")
      .field-container.full-width.padding-top(v-if="hideSegment")
          label {{ $t(`${baseTranslationPath}.teams`) }}
          aiq-tooltip.help-icon(placement="right"
                          :show-after="1000"
                          :content="$t(`${baseTranslationPath}.teams_tooltip`)"
                          )
            fa-icon.question-mark.smlg(:icon="['fas', 'circle-question']")
          .select-box-container
            aiq-select(v-model="record.teams"
                      value-key="id"
                      :max-length="250"
                      multiple
                      :reserve-keyword="false")
              aiq-option(v-for="item in teamOptions", :key="item.id", :label="item.name", :value="item")
      .field-container.full-width.padding-top(v-if="hideSegment")
          label {{ $t(`${baseTranslationPath}.segments`) }}
          aiq-tooltip.help-icon(placement="right"
                          :show-after="1000"
                          :content="$t(`${baseTranslationPath}.segments_tooltip`)"
                          )
            fa-icon.question-mark.smlg(:icon="['fas', 'circle-question']")
          .select-box-container
            aiq-select(v-model="record.segments"
                      value-key="id"
                      :max-length="250"
                      multiple
                      :reserve-keyword="false")
              aiq-option(v-for="item in segments", :key="item.id", :label="item.name", :value="item")

    .field-container.full-width.flex-display.gap-between(v-if="showIncludeSubPages")
      aiq-switch.switch(v-model="include_subpages" :disabled="disableIncludeSubPages")
      label {{ $t(`${baseTranslationPath}.include_subpages`) }}
      aiq-tooltip.help-icon(placement="right"
                          :show-after="1000"
                          :content="$t(`${baseTranslationPath}.include_pages_tooltip`)"
                          )
        fa-icon.question-mark(:icon="['fas', 'circle-question']")
    .field-container.full-width(v-if="isAdvancedCrawlingEnabled && shouldShowAdvancedCrawlingOptions")
      aiq-button.advance-setting-btn(type="text" @click="showAdvancedCrawlingDialog = true") {{ $t(`${baseTranslationPath}.advanced_crawling.advance_setting`) }}
      AdvanceCrawlConfiguration(
        v-if="shouldShowAdvancedCrawlingOptions"
        v-model:visible="showAdvancedCrawlingDialog"
        v-model:config="crawlingConfig"
        :support-schedule="isSchedulingInAdvancedCrawlingEnabled"
        :support-login-wall="supportLoginWallCrawling"
      )
    .field-container.full-width.flex-display.gap-between(v-if="canViewSmartControl")
      aiq-switch.switch(v-model="show_smart_control" :disabled="rules.length === 0 && FEATURE_FLAGS.SHOW_SMART_CONTROL")
      label {{ $t(`settings_tab.smart_drive_tab.smart_control.title`) }}
      aiq-tooltip.help-icon(placement="right"
                          :show-after="1000"
                          :content="$t(`settings_tab.smart_drive_tab.smart_control.tooltip`)"
                          )
        fa-icon.question-mark(:icon="['fas', 'circle-question']")
    .smart-control(v-if="show_smart_control")
      SmartControl(
        :sortedTeams="sortedTeams",
        @rule-satisfied="onRuleSatisfied"
        v-if="canRenderSmartControl"
        :documentRecord="record"
        :rules="rules"
      )
    .foot-area
      .footer
          aiq-button(v-if="hideDelete" type="danger" :disabled="actionInProgress" :plain="true" @click="deleteRecord") {{ deleteInProgress ? $t(`${baseTranslationPath}.delete_in_progress`) : $t(`global.delete`) }}
          aiq-button(@click="navigateAway(false)" :disabled="actionInProgress") {{ $t(`global.cancel`) }}
          aiq-button(type="primary" :disabled="!isSubmittable || actionInProgress" @click="saveRecord") {{ saveInProgress ? $t(`${baseTranslationPath}.save_in_progress`) : $t(`global.save`) }}
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import get from 'lodash/get';
import set from 'lodash/set';
import has from 'lodash/has';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import IQToolMiddlePaneHeader from '@/pages/IQTools/Partials/IQToolMiddlePaneHeader/IQToolMiddlePaneHeader.vue';
import { IMAGE_ACL_TYPES } from '@/constants';
import { confirmBeforeDialogClose } from '@/libs';
import SmartControl from './SmartControl.vue';
import AdvanceCrawlConfiguration from './AdvanceCrawlConfiguration.vue';
import { RECORD_KINDS } from './smartDriveUtil';
import FileUploader from '@/components/FileUtil/FileUploader.vue';
import { RULES_LIMIT } from '@/constants/pagination';
import EditArea from '@/components/EditArea.vue';
const COMPLETE_UPLOAD = 100;
const SUPPORTED_FILE_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/csv', 'text/html'];
const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/tiff', 'image/bmp', 'image/webp', 'image/svg+xml', 'image/svg', 'image/jpg'];

export default {
  name: 'smart-drive-edit',
  components: {
    SmartControl,
    AdvanceCrawlConfiguration,
    IQToolMiddlePaneHeader,
    FileUploader,
    EditArea,
  },
  data() {
    return {
      isNew: false,
      show_smart_control: false,
      imageUrl: '',
      rules: [],
      uploadProgress: {},
      publicCheck: false,
      upload_faq: false,
      itemsToUpload: [],
      disableUpload: false,
      relatedRecords: [],
      timeoutId: null,
      include_subpages: false,
      isAppendItem: false,
      hidePermission: false,
      folderItems: [],
      acl: IMAGE_ACL_TYPES.PUBLIC,
      fileList: [],
      smartControlPayload: null,
      smartControlSatisfied: true,
      smartControlVersionSatisfied: true,
      smartControlExpirationSatisfied: true,
      documentUrl: '',
      sftpPath: '',
      record: {
        name: '',
        description: '',
        file: {
          payload: {},
        },
        url: '',
        teams: [],
        path: '',
        segments: [],
        acl: IMAGE_ACL_TYPES.PUBLIC,
      },
      originalRecord: {},
      originalCheck: {},
      recordKind: '',
      fetchAction: 'documents/getDocumentV2',
      editAction: 'documents/updateDocumentV2',
      createAction: 'documents/createDocumentV2',
      deleteAction: 'documents/deleteDocumentV2',
      visible: true,
      baseTranslationPath: 'settings_tab.smart_drive_tab.edit',
      saveInProgress: false,
      deleteInProgress: false,
      isSmartControlDocument: false,
      latestVersion: null,
      newVersionLabel: '',
      ruleUpdated: false,
      supported_files: [],
      showUploader: false,
      showAdvancedCrawlingDialog: false,
    };
  },
  computed: {
    ...mapState({
      agentProfile: (state) => state.agent.profile,
      FEATURE_FLAGS: state => state.featureFlags.FEATURE_FLAGS,
    }),
    ...mapGetters({
      sortedTeams: 'teams/sortedTeams',
      segments: 'configs/segments',
    }),

    // Update get and set for crawlingConfig to ensure reactivity
    crawlingConfig: {
      get() {
        return get(this.record, 'meta.crawl',
          {
            enabled: true,
            base_url: null,
            frequency: 'daily', // one of: daily, every_2_days, every_3_days, weekly
            extra_headers: {},
            website: {
              sitemap_url: null,
              user_agent: null,
              warmup_nav_path: null,
              crawler_type: 'http', // one of: http, headless
              robot_file_config: {
                mode: 'respect', // one of: respect, ignore
              },
              rules: {
                use_sitemap: false,
                include_paths: [],
                exclude_paths: [],
                max_pages: 500,
                page_delay_seconds: 1.0,
                js_render_wait_ms: 2000,
                follow_links: true,
                max_depth: 4,
                context_pool_size: null,
                full_resource_loading: false,
              },
              auth: {
                enabled: false,
                type: 'form',
                login_url: '',
                selectors: {
                  username: '',
                  password: '',
                  submit: '',
                  remember_me: '',
                  error_block: '',
                },
                success_check: {
                  url_contains: '',
                  url_not_contains: '',
                  selector: '',
                },
                login_wall_signals: [],
                max_login_attempts: 3,
              },
            },
          });
      },
      set(value) {
        if (!this.record.meta) {
          set(this.record, 'meta', {});
        }
        set(this.record.meta, 'crawl', value);
      },
    },

    supportLoginWallCrawling() {
      return this.FEATURE_FLAGS.ADVANCED_CRAWLING_LOGIN_WALL;
    },

    pageTitle() {

      if (this.recordKind === RECORD_KINDS.FILE && this.isNew) {
        return this.$t(`${this.baseTranslationPath}.upload_file`);
      }

      if (this.recordKind === RECORD_KINDS.FILE) {
        return this.$t(`${this.baseTranslationPath}.manage_file`);
      }

      if (this.recordKind === RECORD_KINDS.FOLDER && this.isNew) {
        return this.$t(`${this.baseTranslationPath}.upload_folder`);
      }

      if (this.recordKind === RECORD_KINDS.ADDITONALFOLDER && (this.isNew || this.isAppendItem)) {
        return this.$t(`${this.baseTranslationPath}.add_folder`);
      }

      if (this.recordKind === RECORD_KINDS.FOLDER || this.recordKind === RECORD_KINDS.ADDITONALFOLDER) {
        return this.$t(`${this.baseTranslationPath}.manage_folder`);
      }

      if (this.recordKind === RECORD_KINDS.SFTP) {
        return this.$t(`${this.baseTranslationPath}.add_sftp`);
      }

      if (this.recordKind === RECORD_KINDS.SHAREPOINT) {
        return this.$t(`${this.baseTranslationPath}.manage_sharepoint`);
      }

      if (this.recordKind === RECORD_KINDS.HUBSPOT && this.isNew) {
        return this.$t(`${this.baseTranslationPath}.add_hubspot`);
      }

      if (this.recordKind === RECORD_KINDS.HUBSPOT) {
        return this.$t(`${this.baseTranslationPath}.manage_hubspot`);
      }

      if ((this.recordKind === RECORD_KINDS.URL || this.recordKind === RECORD_KINDS.HTML || this.recordKind === RECORD_KINDS.URLLIST) && !this.isNew && !this.isAppendItem) {
        return this.$t(`${this.baseTranslationPath}.manage_link`);
      }

      return this.$t(`${this.baseTranslationPath}.upload_url`);

    },
    actionInProgress() {
      return this.saveInProgress || this.deleteInProgress;
    },

    canRenderSmartControl() {
      return this.isNew || this.record.name !== '';
    },

    shouldShowAdvancedCrawlingOptions() {
      return (
        (
          (this.recordKind === RECORD_KINDS.URL || this.recordKind === RECORD_KINDS.URLLIST)
          && this.upload_faq
        )
        || this.recordKind === RECORD_KINDS.SHAREPOINT
        || this.recordKind === RECORD_KINDS.SFTP
        || this.recordKind === RECORD_KINDS.HUBSPOT
      );
    },

    isSubmittable() {
      const record_changed = (
        !isEqual(this.originalRecord, this.record) ||
        this.publicCheck !== this.originalCheck.publicCheck ||
        this.upload_faq !== this.originalCheck.upload_faq ||
        this.include_subpages !== this.originalCheck.include_subpages ||
        this.ruleUpdated
      );
      if (this.recordKind === RECORD_KINDS.FILE) {
        return this.getFiles().length > 0 && !!this.getFiles()[0].id && !!this.record.name && record_changed;
      }
      if ([RECORD_KINDS.URL, RECORD_KINDS.HTML, RECORD_KINDS.HUBSPOT].includes(this.recordKind)) {
        return !!this.documentUrl && !!this.record.name && record_changed;
      }
      if (this.recordKind === RECORD_KINDS.SFTP) {
        return !!this.sftpPath && !!this.record.name && record_changed;
      }

      return !!this.record.name && record_changed;
    },
    canViewSmartControl() {
      const smart_control_feat_flag = this.FEATURE_FLAGS.SHOW_SMART_CONTROL;
      const has_acl = this.canView('/settings/smartdrive/rules/assign');
      const isFile = this.recordKind === RECORD_KINDS.FILE;
      const isNotSmartControl = !has(this.record, 'meta.smart_control');
      return smart_control_feat_flag && has_acl && isFile && isNotSmartControl;
    },
    hideSegment() {
      return !(this.isAppendItem || this.hidePermission);
    },
    disableIncludeSubPages() {
      return !this.upload_faq || !this.FEATURE_FLAGS.GENERATIVE_FAQ;
    },
    hideDelete() {
      if (this.isAppendItem) {
        return false;
      }
      return !this.isNew;
    },
    showIncludeSubPages() {
      if (this.isAppendItem && (this.recordKind === RECORD_KINDS.URL || this.recordKind === RECORD_KINDS.HTML)) {
        return true;
      }
      if (this.hidePermission) {
        return false;
      }
      if (!this.isNew) {
        return false;
      }
      return (
        this.recordKind === RECORD_KINDS.URL ||
        this.recordKind === RECORD_KINDS.HTML
      );
    },
    acceptedFileType() {
      if (this.recordKind === RECORD_KINDS.FILE) {
        if (this.FEATURE_FLAGS.ENABLE_SMARTDRIVE_IMAGE) {
          return 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv,text/html,image/*';
        } else {
          return 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv,text/html';
        }
      } else {
        return '*/*';
      }
    },
    isSharePoint() {
      const sharepointUrl = get(this.record, 'meta.sharepoint.url', '');
      const sharepointId = get(this.record, 'meta.sharepoint.id', '');
      if (sharepointId.length > 0 || sharepointUrl.length > 0) {
        return true;
      }
      return false;
    },
    uploadForbidden() {
      if (this.isAppendItem) {
        return false;
      }

      if (this.recordKind === RECORD_KINDS.FILE && !this.isNew) {
        return true;
      }

      return this.recordKind === RECORD_KINDS.FOLDER && !this.isNew;
    },
    hasSelectedItem() {
      return this.itemsToUpload.length > 0;
    },
    disableUrl() {
      if (this.isAppendItem) {
        return false;
      }
      return !this.isNew;
    },
    showFileInput() {
      if (this.hidePermission) {
        return false;
      }
      if (
        this.recordKind === RECORD_KINDS.URL ||
        this.recordKind === RECORD_KINDS.HTML ||
        this.recordKind === RECORD_KINDS.SFTP ||
        this.recordKind === RECORD_KINDS.HUBSPOT
      ) {
        return false;
      }

      if (
        this.recordKind === RECORD_KINDS.ADDITONALFOLDER &&
        this.isAppendItem
      ) {
        return false;
      }
      if (this.isAppendItem) {
        return true;
      }

      if (this.recordKind === RECORD_KINDS.FOLDER && !this.isNew) {
        return false;
      }

      if (this.recordKind === RECORD_KINDS.FOLDER) {
        return true;
      }

      if (this.recordKind === RECORD_KINDS.FILE) {
        return true;
      }

      return false;
    },
    tinyInfo() {
      if (this.isSharePoint) {
        return this.$t(
          `${this.baseTranslationPath}.upload_to_sharepoint_forbidden`,
        );
      }
      return '';
    },

    isPublic() {
      if (!this.isNew) {
        return (
          get(this.record, 'file.payload.acl', 'private') !==
          IMAGE_ACL_TYPES.PUBLIC
        );
      }
      return this.publicCheck;
    },
    teamOptions() {
      return this.canView('/users/filters/show_all_teams')
        ? this.sortedTeams
        : this.agentProfile.teams;
    },
    isAdvancedCrawlingEnabled() {
      return this.FEATURE_FLAGS.ADVANCED_CRAWLING && this.canView('/settings/smartdrive/crawler_configuration');
    },
    isSchedulingInAdvancedCrawlingEnabled() {
      return this.FEATURE_FLAGS.ADVANCED_CRAWLING_SCHEDULING;
    },
  },

  async mounted() {
    this.supported_files = this.FEATURE_FLAGS.ENABLE_SMARTDRIVE_IMAGE ? [...SUPPORTED_IMAGE_TYPES, ...SUPPORTED_FILE_TYPES] : SUPPORTED_FILE_TYPES;
    await this.initialize();
    if (!this.record.segments) {
      this.record.segments = [];
    }
    this.sftpPath = get(this.record, 'meta.path', '');
    this.showUploader = true;
    if (this.canViewSmartControl) {
      await this.fetchRules();
    }

  },
  methods: {
    async fetchRules() {
      const params = {
        limit: RULES_LIMIT,
      };

      const rules = await this.$store.dispatch(
        'smartControl/getRuleList',
        [params, true],
      );

      this.rules = get(rules, 'data.models', []);
    },
    async initialize() {
      const params = get(this.$route, 'params.id', '');
      const query = get(this.$route, 'query.type', '');
      this.hidePermission = get(this.$route, 'query.hidePermission', false);
      this.isNew = params === 'new';
      this.recordKind =
        Object.values(RECORD_KINDS).find((kind) => query === kind) || '';
      this.isAppendItem = get(this.$route, 'query.appendItem', false);
      if (this.recordKind === '') {
        this.navigateAway(true);
      }

      if (!this.isNew) {
        this.record = (
          await this.$store.dispatch(this.fetchAction, [
            params,
            this.teamOptions.map((x) => x.id),
          ])
        ).data;
        this.upload_faq = get(this.record, 'meta.genai', false);
        let acl;
        if (get(this.record, 'object_type') === RECORD_KINDS.FOLDER ||
          get(this.record, 'object_type') === RECORD_KINDS.URL ||
          get(this.record, 'object_type') === RECORD_KINDS.HTML ||
          get(this.record, 'object_type') === RECORD_KINDS.SHAREPOINT ||
          get(this.record, 'object_type') === RECORD_KINDS.URLLIST ||
          get(this.record, 'object_type') === RECORD_KINDS.HUBSPOT
        ) {
          acl = get(this.record, 'meta.acl', 'private');
        } else {
          acl = get(this.record, 'file.payload.acl', 'private');
        }
        if (this.recordKind === RECORD_KINDS.FILE && this.record.file) {
          this.fileList = [{
            file_id: this.record.file.id,
            ...this.record.file.payload,
          }];
        }
        this.publicCheck = acl === IMAGE_ACL_TYPES.PUBLIC ? true : false;
        //remove duplicate teams
        this.record.teams = this.record.teams.filter(
          (team, index) =>
            this.record.teams.findIndex((t) => t.id === team.id) === index,
        );
        if (
          this.recordKind === RECORD_KINDS.URL ||
          this.recordKind === RECORD_KINDS.HTML ||
          this.recordKind === RECORD_KINDS.URLLIST ||
          this.recordKind === RECORD_KINDS.HUBSPOT
        ) {
          if (get(this.record, 'file.payload.url')) {
            this.documentUrl = get(this.record, 'file.payload.url');
          } else {
            this.documentUrl = get(this.record, 'meta.url', '');
          }

          if (has(this.record, 'meta.crawl')) {
            this.crawlingConfig = get(this.record, 'meta.crawl');
          }
        }
        if (
          this.recordKind === RECORD_KINDS.URL ||
          this.recordKind === RECORD_KINDS.HTML ||
          this.recordKind === RECORD_KINDS.URLLIST
        ) {
          this.include_subpages = get(this.record, 'meta.genai_crawl', false);
        }
      }
      if (this.isAppendItem) {
        this.record.name = '';
      }
      this.originalRecord = cloneDeep(this.record);
      this.originalCheck = {
        publicCheck: this.publicCheck,
        include_subpages: this.include_subpages,
        upload_faq: this.upload_faq,
      };
      const isNewUpload = get(this.record, 'file.payload.name', '') === '';
      if (!isNewUpload) {
        this.uploadProgress = {
          uploadPercent: COMPLETE_UPLOAD,
          uploadStatus: 'success',
          progressBarStatus: 'complete',
        };
      }
    },

    onRuleSatisfied(data) {
      const selectedRule = data.selectedRule;
      this.smartControlPayload = selectedRule;
      if (selectedRule && selectedRule.payload) {
        const parsedFrequencyCycle = parseInt(selectedRule.payload.frequency_cycle, 10);
        selectedRule.payload.frequency_cycle = Number.isNaN(parsedFrequencyCycle) ? 1 : parsedFrequencyCycle;
      }
      this.smartControlSatisfied = data.isRuleSatisfied;
      this.smartControlVersionSatisfied = data.versionSatisfied;
      this.smartControlExpirationSatisfied = data.expirySatisfied;
      this.ruleUpdated = true;
    },


    closeEditor() {
      this.visible = false;
    },
    getFiles() {
      if (this.recordKind === RECORD_KINDS.URL) {
        return [];
      }
      if (this.recordKind === RECORD_KINDS.FILE) {
        if (this.fileList.length > 0) {
          return [
            {
              id: this.fileList[0].file_id,
              path: this.fileList[0].filename,
            },
          ];
        } else {
          return [];
        }
      }
      if (this.recordKind === RECORD_KINDS.FOLDER) {
        return this.fileList.map((item) => {
          return {
            id: item.file_id,
            path: item.webkitRelativePath,
          };
        });
      }
      return [];
    },
    getTeams() {
      return this.record.teams.map((team) => team.id);
    },
    getSegments() {
      return this.record.segments.map((segment) => segment.id);
    },
    async saveRecord() {
      let data;
      let parent_id;
      if (this.isAppendItem) {
        parent_id = parseInt(get(this.$route, 'params.id', ''));
      } else {
        parent_id = get(this.record, 'parent_id');
      }

      if (!this.smartControlSatisfied) {
        this.$aiq.notify.error(
          this.$t(`${this.baseTranslationPath}.smart_control_error`),
        );
        return;
      }

      if (!this.smartControlVersionSatisfied) {
        this.$aiq.notify.error(
          this.$t(`${this.baseTranslationPath}.smart_control_version_error`),
        );
        return;
      }

      if (!this.smartControlExpirationSatisfied) {
        this.$aiq.notify.error(
          this.$t(`${this.baseTranslationPath}.smart_control_expiration_error`),
        );
        return;
      }

      if (
        this.recordKind === RECORD_KINDS.URL ||
        this.recordKind === RECORD_KINDS.HTML ||
        this.recordKind === RECORD_KINDS.SFTP ||
        this.recordKind === RECORD_KINDS.HUBSPOT
      ) {
        data = {
          type: this.recordKind,
          title: this.record.name,
          url: this.documentUrl,
          description: this.record.description || '',
          permissions: this.getTeams().length > 0 ? true : false,
          acl: this.publicCheck ? IMAGE_ACL_TYPES.PUBLIC : IMAGE_ACL_TYPES.PRIVATE,
          teams: this.getTeams(),
          segments: this.getSegments(),
          path: this.sftpPath,
          upload_faq: this.upload_faq,
          ...(this.recordKind === RECORD_KINDS.SFTP || this.recordKind === RECORD_KINDS.HUBSPOT ? {} : {
            include_subpages: this.include_subpages,
          }),
          ...(parent_id ? { parent_id } : {}),
        };

      } else {
        data = {
          files: this.getFiles(),
          type:
            this.recordKind === RECORD_KINDS.ADDITONALFOLDER
              ? RECORD_KINDS.FOLDER
              : this.recordKind,
          title: this.record.name,
          url: this.documentUrl,
          description: this.record.description || '',
          permissions: this.getTeams().length > 0 ? true : false,
          segments: this.getSegments(),
          acl: this.publicCheck ? IMAGE_ACL_TYPES.PUBLIC : IMAGE_ACL_TYPES.PRIVATE,
          teams: this.getTeams(),
          upload_faq: this.upload_faq,
          ...(parent_id ? { parent_id } : {}),
          ...(this.canViewSmartControl ? { smart_control: !isEmpty(this.smartControlPayload) } : {}),
        };
      }

      if (this.shouldShowAdvancedCrawlingOptions) {
        data.meta = {
          crawl: this.crawlingConfig,
        };
      }

      try {
        this.saveInProgress = true;
        if (!this.isNew && !this.isAppendItem) {
          const payload = {
            id: this.record.id,
            payload: data,
          };
          await this.$store.dispatch(this.editAction, payload);

          //attach smart control rule if exist
          if (this.smartControlPayload) {
            const data = {
              id: get(this.record, 'id'),
              data: {
                delete_document_on_error: false,
                ...this.smartControlPayload,
              },
            };
            await this.$store.dispatch('smartControl/updateSmartControl', data,
            );
          }
          this.$aiq.notify.success(
            this.$t(`${this.baseTranslationPath}.saved_successfully`),
          );
          this.navigateAway(true);
        } else {
          const payload = data;
          const res = await this.$store.dispatch(this.createAction, payload);
          const id = get(res, 'data.id');
          //attach smart control rule if exist
          if (this.smartControlPayload) {
            await this.$store.dispatch('smartControl/attachSmartControl', {
              id,
              data: {
                delete_document_on_error: true,
                ...this.smartControlPayload,
              },
            });
          }
          this.$aiq.notify.success(
            this.$t(`${this.baseTranslationPath}.saved_successfully`),
          );
          this.navigateAway(true);
        }
      } catch (e) {
        const errorMessage = get(
          e,
          'response.data.message',
          this.$t(`${this.baseTranslationPath}.unable_to_save`),
        );
        this.$aiq.notify.error(errorMessage);
      } finally {
        this.saveInProgress = false;
        this.ruleUpdated = false;
      }
    },


    getRecordName(name) {
      if (this.record.name === '') {
        this.record.name = name;
      }
    },
    getFileList(files) {
      this.fileList = files;
    },

    async deleteConfirm() {
      try {
        await this.$aiq.confirm(
          this.$t(`${this.baseTranslationPath}.delete_confirm.title`),
          this.$t(`${this.baseTranslationPath}.delete_confirm.message`),
        );
        return true;
      } catch {
        return false;
      }
    },
    async deleteRecord() {
      try {
        const confirm = await this.deleteConfirm();
        if (!confirm) {
          return;
        }

        this.deleteInProgress = true;
        await this.$store.dispatch(this.deleteAction, this.record.id);
        this.$aiq.notify.success(
          this.$t(`${this.baseTranslationPath}.deleted_successfully`),
        );
        this.navigateAway(true);
      } catch (err) {
        this.$aiq.notify.error(
          this.$t(`${this.baseTranslationPath}.unable_to_delete`),
        );
      } finally {
        this.deleteInProgress = false;
      }
    },
    handleBeforeClose() {
      if (!isEqual(this.originalRecord, this.record)) {
        confirmBeforeDialogClose(this, this.closeEditor);
      } else if (this.publicCheck !== this.originalCheck.publicCheck ||
      this.upload_faq !== this.originalCheck.upload_faq ||
      this.include_subpages !== this.originalCheck.include_subpages) {
        confirmBeforeDialogClose(this, this.closeEditor);
      }
      else {
        this.closeEditor();
      }
    },

    async validateForChanges() {
      if (isEqual(this.originalRecord, this.record)) {
        return true;
      } else {
        try {
          await this.$aiq.confirm(
            this.$t('modal_editor.before_close_confirm.title'),
            this.$t('modal_editor.before_close_confirm.message', {
              name: this.record.name,
              defaultValue: 'modal_editor.before_close_confirm.message',
            }),
          );
          return true;
        } catch {
          return false;
        }
      }
    },
    async navigateAway(val = false) {
      if (!val) {
        const changes = await this.validateForChanges();
        if (!changes) {
          return;
        }
      }
      // editor is contained by a route
      const id = get(this, '$route.params.id');
      if (id && this.$route.matched.length > 1) {
        // get the parent path
        const currentPath = this.$route.path;
        const parentPath = currentPath.substring(
          0,
          currentPath.lastIndexOf('/'),
        );
        // get parant name if exist
        const name = get(this.$route, 'meta.parentName');
        // move to the parent
        this.$router.push(name ? { name } : parentPath);
      }
    },
    getTitle() {
      if (!this.isNew) {
        return this.$t(`${this.baseTranslationPath}.edit_${this.recordKind}`);
      } else {
        return this.$t(`${this.baseTranslationPath}.upload_${this.recordKind}`);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.field-container {
  margin-top: 10px;
}
.smlg {
    margin-left: 10px;
}

.question-mark {
  padding-top: 3px;
}

.switch {
  margin-right: 10px;
}
.page-header-smart-drive {
  padding-top: 20px;
}

.file-input {
  flex: 1;
}
.required-label {
  display: flex;
  justify-content: flex-end;
  color: red;
}

.foot-area {
  margin-top: 20px;
}
.footer {
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.flex-display {
  display: flex;
  align-items: flex-start;
  width: 100%;
}

.file-list {
  margin-left: auto;
}

.file-list {
  margin-top: 20px;
}

.iq-ico-explanation {
  margin-top: 3px;
}
.padding-top {
  padding-top: 10px;
}

.tiny-info {
  color: #475669;
  font-size: 10px;
  font-weight: 80;
}
.gap-between {
  padding-top: 20px;
  gap: 10px;
}

:deep(.el-upload) {
  position: relative;
  width: 100%;
  height: 100%;
}

.img-gap {
  gap: 20%;
}

.img-uploader {
    width: 400px;
    height: 200px;
    border-radius: 4px;
    border: 1px dashed #BFCBD9;
    background-color: #F9FAFC;
    overflow: hidden;

    &:hover {
      border-color: #20a0ff;
    }

  }

.img-uploader-preview {
    width: 100%;
    height: 100%;
    background-position: center center;
    background-size: cover;
}

.img-uploader-container {
    margin-bottom: 10px;
    color: #475669;
    position: absolute;
    width: 130px;
    font-size: 14px;
    font-weight: 100;

    label {
      display: block;
      margin-bottom: 10px;
      color: #475669;
      font-size: 14px;
      font-weight: 100;
      text-align: left;
    }
  }
.img-uploader-add {
    position: relative;
    width: 100%;
    height: 100%;

    &::before {
      position: absolute;
      top: 50%;
      left: 50%;
      display: block;
      width: 28px;
      height: 2px;
      content: '';
      border-radius: 2px;
      transform: translate(-50%, -50%);
      background-color: #BFCBD9;
    }
    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      display: block;
      width: 28px;
      height: 2px;
      content: '';
      border-radius: 2px;
      transform: translate(-50%, -50%) rotate(90deg);
      background-color: #BFCBD9;
    }
  }
:deep(.el-switch.is-checked .el-switch__core) {
  background-color: #5993FF !important;
  border-color: #5993FF !important;
}
</style>
